// @ts-strict-ignore
import type { MutableRefObject } from 'react';
import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { getNumericPathIndices } from 'flow/service/actions/actions';
import type { AttributeComponent, Expression } from '~/dashboard/views/workflows/store/engine/types';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import {
  selectPendingGroup,
  selectWorkflowData,
} from '~/dashboard/views/workflows/store/redux/selectors';
import { resetPendingGroup } from '~/dashboard/views/workflows/store/redux/slice';
import { CaretPositionSuffixTypes, RuleEditorDropdownMode, RuleEditorDropdownModeCategory } from '../NodeEditorDrawer/types';
import type { CaretPositionGetterReturnValues, UserActionObject } from '../NodeEditorDrawer/types';
import { flattenExpressionIndices, getPossibleGroupClosingPathIndex } from '../expressions/helpers';
import { getDropdownModeCategory, getFlattenedPathInCaretPositionFormat, scrubInsertionModeFromCaret } from './helpers';
import { getAllPossibleGroupClosingPaths, getNextExpressionCaretPosition, getPreviousExpressionCaretPosition } from './expression';
import { flattenMLAComponentIndices } from '../../../service/mla';
import { CaretIndexType, mlaCTACaretPositionString } from './constants';

export const getDataRoutePositionString = (newPosIndex: number, actionPublicId: string) => {
  if (actionPublicId.includes('service')) {
    // we want to position caret to focus on svc attribute config dropdown
    return `${newPosIndex}-1${CaretPositionSuffixTypes.DATAROUTE}`;
  }

  return `${newPosIndex}-0${CaretPositionSuffixTypes.DATAROUTE}${CaretPositionSuffixTypes.CARET_INSERTION}`;
};

export const useCaretControl = ({
  caretType,
  expressionData,
  mlaData,
  sourceId,
  ruleEditorPanelBoxContainerRef,
  mlaEditorEnabled,
  isMatrixModelFocused,
}: {
  caretType: CaretIndexType;
  expressionData?: Expression;
  mlaData?: AttributeComponent[];
  sourceId: string | number;
  ruleEditorPanelBoxContainerRef: MutableRefObject<any>;
  mlaEditorEnabled?: boolean;
  isMatrixModelFocused?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const { dataRoutes } = useAppSelector(selectWorkflowData);

  // stores an active dropdown input value for tracking if user was typing/deleting a character or pressed backspace to delete a whole expression item
  const [activeInputValue, setActiveInputValue] = useState<{
    value: string;
    path: string;
    initialValue: boolean;
  }>({
    value: '',
    path: '',
    initialValue: false,
  });

  const pendingGroup = useAppSelector(selectPendingGroup);

  const pendingGroupPathIndices = useMemo(() => (
    getNumericPathIndices(pendingGroup.path)
  ), [pendingGroup.path]);

  const pendingGroupPathInFlatCaretIndexFormat: string | undefined = useMemo(() => {
    const { path } = pendingGroup;
    if (!path) return undefined;
    if (path === '-1') return '-1';
    const pendingGroupPath = pendingGroupPathIndices
      ? pendingGroupPathIndices.join('-')
      : undefined;
    return pendingGroupPath;
  }, [pendingGroup, pendingGroupPathIndices]);

  const pendingGroupMaxRange = useMemo(() => {
    const { path, side } = pendingGroup;

    if (expressionData && path) {
      const {
        parentOfGroupOpeningPath,
        itemsWithinMaxRange,
      } = getPossibleGroupClosingPathIndex({
        expressions: expressionData,
        pendingGroupPathIndices,
        pendingGroupSide: side,
      });

      const allPossibleGroupClosingPaths = getAllPossibleGroupClosingPaths({
        itemsWithinMaxRange,
        pendingGroupPathIndices,
        pendingGroupSide: side,
        parentOfGroupOpeningPath,
      });

      return {
        parentOfGroupOpeningPath,
        itemsWithinMaxRange,
        indexOfMaxRangeLastItem: itemsWithinMaxRange.length - 1,
        allPossibleGroupClosingPaths,
      };
    }

    return {
      parentOfGroupOpeningPath: '',
      itemsWithinMaxRange: [],
      indexOfMaxRangeLastItem: 0,
      allPossibleGroupClosingPaths: [],
    };
  }, [
    expressionData,
    pendingGroup,
    pendingGroupPathIndices,
  ]);

  const [
    morphingCaretPosition,
    setMorphingCaretPosition,
  ] = useState<string>('');

  const ruleEditorFlatCaretIndices: string[] = useMemo(() => {
    if (expressionData) {
      return flattenExpressionIndices({
        expressionToFlatten: expressionData,
        prefixIndex: undefined,
        dataRoutes,
        pendingGroupPath: pendingGroupPathInFlatCaretIndexFormat,
        pendingParanthesisSide: pendingGroup.side,
      });
    }

    if (mlaData) {
      return [
        ...flattenMLAComponentIndices({ mlaData }),
        mlaCTACaretPositionString,
      ];
    }

    return [];
  }, [
    dataRoutes, expressionData, mlaData,
    pendingGroupPathInFlatCaretIndexFormat,
    pendingGroup,
  ]);

  const getFlattenedCaretPositionIndex = ({
    itemPath,
    mode,
    type,
  }: {
    itemPath: string;
    mode?: RuleEditorDropdownMode;
    type: CaretIndexType;
  }) => {
    if (type === CaretIndexType.MLA) {
      return ruleEditorFlatCaretIndices[Number(itemPath)];
    }
    if (mode !== RuleEditorDropdownMode.CARET_EXPRESSION) { // if expression or data route is undefined, this dropdown is a caret dropdown for new insertion
      if (getDropdownModeCategory(mode) === RuleEditorDropdownModeCategory.DATAROUTE) { // data route that has service as a source will require a pair of caret indices and if so, we need to generate the right index- either -0 or -1
        const path = `${itemPath}${mode === RuleEditorDropdownMode.DATAROUTE_SOURCE ? '[0]' : '[1]'}`;
        const flattenedPath = getFlattenedPathInCaretPositionFormat(path);
        return `${flattenedPath}${CaretPositionSuffixTypes.DATAROUTE}`;
      }

      if (getDropdownModeCategory(mode) === RuleEditorDropdownModeCategory.GROUP) { // if dropdown is either group start or group end, caret position needs to reflect that
        const flattenedPath = getFlattenedPathInCaretPositionFormat(itemPath);
        return `${flattenedPath}!${mode}`;
      }

      return getFlattenedPathInCaretPositionFormat(itemPath);
    }

    if (getDropdownModeCategory(mode) === RuleEditorDropdownModeCategory.GROUP
      && pendingGroup.path === itemPath) {
      const flattenedPath = getFlattenedPathInCaretPositionFormat(itemPath);
      return `${flattenedPath}!${mode}`;
    }

    return undefined;
  };

  const previousMorphingCaretPosition = useRef('');

  const [
    pendingMorphingCaretPositionUpdateValue,
    setPendingMorphingCaretPositionUpdateValue,
  ] = useState<string>(undefined);

  useEffect(() => {
    // if pendingMorphingCaretPositionUpdateValue is not undefined, we do something
    // because this means caret position has to be updated as a result of data update (data insertion or deletion in expression data)
    // and that causes indices to be shifted/regenerated, meaning we have to execute this after flatCaretIndices are updated.
    // also this value would be simply a numeric index of flatCaretIndices, because the string value of that would've been changed.
    if (pendingMorphingCaretPositionUpdateValue) {
      // it'll be an index number where we should look at in the flat indices
      let newPosition = pendingMorphingCaretPositionUpdateValue === '-1'
        ? '-1'
        : `${ruleEditorFlatCaretIndices[
          Number(pendingMorphingCaretPositionUpdateValue)
        ]}`;

      // if CaretIndexType.RULES, it uses newPosition as is
      if (caretType === CaretIndexType.EXPRESSION) {
        // for inserting new data into expression
        newPosition = `${newPosition}${CaretPositionSuffixTypes.CARET_INSERTION}`;
      }
      if (caretType === CaretIndexType.MLA) {
        // the value will be set and therefore caret will get updated,
        // so we "predict" here and set position ahead of time
        newPosition = `${newPosition}${CaretPositionSuffixTypes.MLA_VALUE_SET}`;
      }

      setPendingMorphingCaretPositionUpdateValue(undefined);
      previousMorphingCaretPosition.current = newPosition;
      setMorphingCaretPosition(newPosition);
    }
  }, [
    caretType, ruleEditorFlatCaretIndices, pendingMorphingCaretPositionUpdateValue,
  ]);

  useEffect(() => {
    if (morphingCaretPosition) {
      previousMorphingCaretPosition.current = morphingCaretPosition;
      return;
    }
    let newPosition = '';
    // initialize caret position upon editor initialization
    if (previousMorphingCaretPosition.current) {
      newPosition = `${previousMorphingCaretPosition.current}`;
      previousMorphingCaretPosition.current = '';
    }

    if (caretType === CaretIndexType.EXPRESSION) {
      // 0 references the first position / caret in an output expression
      const newPositionIndex = ruleEditorFlatCaretIndices?.length > 0 ? '' : '-1';
      newPosition = `${newPositionIndex}${CaretPositionSuffixTypes.CARET_INSERTION}`;
    } else if (ruleEditorFlatCaretIndices?.length === 0) {
      newPosition = '0';
    } else {
      newPosition = mlaData
        ? ruleEditorFlatCaretIndices[0] // in MLA editor, we want to autofocus the first one when opening
        : '';
    }
    setMorphingCaretPosition(newPosition);
  }, [caretType, expressionData, mlaData,
    morphingCaretPosition, ruleEditorFlatCaretIndices]);

  useEffect(() => {
    if (!sourceId) return;
    if (caretType === CaretIndexType.MLA) setMorphingCaretPosition('');
  }, [sourceId, caretType]);

  const indexOfCurrentCaretPositionInFlatIndices: number | undefined = useMemo(() => {
    const caretPosition = morphingCaretPosition
      ? scrubInsertionModeFromCaret(morphingCaretPosition)
      : (ruleEditorFlatCaretIndices.at(0) || undefined);

    return ruleEditorFlatCaretIndices.findIndex(indexItem => (caretPosition === indexItem));
  }, [morphingCaretPosition, ruleEditorFlatCaretIndices]);

  const getPreviousCaretPosition = useCallback(({ userActionObject }: {
    userActionObject?: UserActionObject;
  }) => {
    if (expressionData) {
      return getPreviousExpressionCaretPosition({
        indexOfCurrentCaretPositionInFlatIndices,
        morphingCaretPosition,
        ruleEditorFlatCaretIndices,
        userActionObject,
      });
    }

    if (mlaData) {
      // find the next closest index token with "valueSet"
      const nextIndexItem = mlaData
        ? ruleEditorFlatCaretIndices
          .slice(0, indexOfCurrentCaretPositionInFlatIndices)
          .reverse()
          .find(indexToken => (
            indexToken.includes(CaretPositionSuffixTypes.MLA_VALUE_SET)
            || indexToken.includes(CaretPositionSuffixTypes.MLA_REQUIRED)
          ))
        : ruleEditorFlatCaretIndices[indexOfCurrentCaretPositionInFlatIndices - 1];

      // @todo: ruleEditorFlatCaretIndices.at(0) is a temporary treatment
      // add a new line logic should set this to a new threshold insertion caret.
      return {
        newPositionString: nextIndexItem || ruleEditorFlatCaretIndices.at(0),
        newPositionIndexForDelayedUpdate: undefined,
      };
    }

    return {
      newPositionString: '',
      newPositionIndexForDelayedUpdate: undefined,
    };
  }, [
    expressionData,
    mlaData,
    indexOfCurrentCaretPositionInFlatIndices,
    morphingCaretPosition,
    ruleEditorFlatCaretIndices,
  ]);

  const getNextCaretPosition = useCallback(({ userActionObject }: {
    userActionObject?: UserActionObject;
  }) => {
    if (expressionData) {
      return getNextExpressionCaretPosition({
        pendingGroupPathInFlatCaretIndexFormat,
        indexOfCurrentCaretPositionInFlatIndices,
        getDataRoutePositionString,
        morphingCaretPosition,
        ruleEditorFlatCaretIndices,
        userActionObject,
      });
    }

    if (mlaData) {
      const nextIndex = indexOfCurrentCaretPositionInFlatIndices + 1;

      const nextIndexItem = !mlaData
        ? ruleEditorFlatCaretIndices[nextIndex]
        : ruleEditorFlatCaretIndices // find the next closest index token with "valueSet"
          .slice(nextIndex)
          .find(indexToken => (
            indexToken.includes(CaretPositionSuffixTypes.MLA_VALUE_SET)
          || indexToken.includes(CaretPositionSuffixTypes.MLA_REQUIRED)
          ));

      // when updating an existing and/or operator in rules, the "nextIndexItem"
      // is using an outdated position string that includes the old operator
      // so we track the index instead which is handled in useEffect on line 235^
      // for when we have to update position after flat indices are regenerated
      const newPositionIndexForDelayedUpdate = !mlaData ? nextIndex : undefined;

      let newPositionString = ''; // for when we can update position directly
      if (nextIndexItem && (
        indexOfCurrentCaretPositionInFlatIndices !== -1
        && nextIndexItem !== ruleEditorFlatCaretIndices[0]
      )) {
        newPositionString = nextIndexItem;
      } else if (morphingCaretPosition.startsWith('cta-')) {
        newPositionString = ruleEditorFlatCaretIndices.at(0);
      } else {
        newPositionString = mlaCTACaretPositionString;
      }
      return {
        newPositionString,
        newPositionIndexForDelayedUpdate,
      };
    }

    return {
      newPositionString: '',
      newPositionIndexForDelayedUpdate: undefined,
    };
  }, [
    expressionData,
    mlaData,
    indexOfCurrentCaretPositionInFlatIndices,
    morphingCaretPosition,
    ruleEditorFlatCaretIndices,
    pendingGroupPathInFlatCaretIndexFormat,
  ]);

  const getNewPositionToJump = useCallback(({ userActionObject }: {
    userActionObject?: UserActionObject;
  }) => {
    if (mlaData && userActionObject.mode === 'add_filter') {
      const nextIndexItemIndex = ruleEditorFlatCaretIndices
        .findIndex(indexToken => (indexToken === userActionObject.value));

      return {
        // for when we can update position directly
        newPositionString: String(nextIndexItemIndex),
        // for when we have to update position after flat indices are regenerated
        newPositionIndexForDelayedUpdate: String(nextIndexItemIndex),
      } as CaretPositionGetterReturnValues;
    }

    return {
      newPositionString: '',
      newPositionIndexForDelayedUpdate: undefined,
    } as CaretPositionGetterReturnValues;
  }, [mlaData, ruleEditorFlatCaretIndices]);

  const onCaretNavigationKeyDown = useCallback(event => {
    if (
      event?.key?.startsWith('Backspace')
      || event?.key?.startsWith('Delete')
      || activeInputValue.value.length > 0
      || (mlaEditorEnabled && expressionData)
      || isMatrixModelFocused
    ) return;

    switch (event.key) {
      case 'ArrowLeft': {
        const { newPositionString } = getPreviousCaretPosition({});
        setMorphingCaretPosition(newPositionString);
      }
        break;
      case 'ArrowRight': {
        const { newPositionString } = getNextCaretPosition({});
        setMorphingCaretPosition(newPositionString);
      }
        break;
      case 'Tab':
        event.preventDefault();
        if (event.shiftKey) {
          const { newPositionString } = getPreviousCaretPosition({});
          setMorphingCaretPosition(newPositionString);
        } else {
          const { newPositionString } = getNextCaretPosition({});
          setMorphingCaretPosition(newPositionString);
        }
        break;
    }
  }, [
    activeInputValue,
    mlaEditorEnabled,
    expressionData,
    getPreviousCaretPosition,
    setMorphingCaretPosition,
    getNextCaretPosition,
    isMatrixModelFocused,
  ]);

  useEffect(() => {
    const refElement = ruleEditorPanelBoxContainerRef?.current;
    refElement?.addEventListener('keydown', onCaretNavigationKeyDown);
    return () => refElement?.removeEventListener('keydown', onCaretNavigationKeyDown);
  }, [ruleEditorFlatCaretIndices, onCaretNavigationKeyDown, ruleEditorPanelBoxContainerRef]);

  /**
  * ONLY when the focusedAction public ID changes
  * but the drawer did not close or reset (clicking another node):
  * need to manually reset all useCaretControl states.
  */
  useEffect(() => {
    if (sourceId) {
      setActiveInputValue({
        value: '',
        path: '',
        initialValue: false,
      });
      setMorphingCaretPosition('');
      if (caretType === CaretIndexType.EXPRESSION) {
        dispatch(resetPendingGroup());
      }
      setPendingMorphingCaretPositionUpdateValue(undefined);
    }
  }, [sourceId, dispatch, caretType]);

  return {
    ruleEditorFlatCaretIndices,
    morphingCaretPosition,
    setMorphingCaretPosition,
    getFlattenedCaretPositionIndex,
    pendingMorphingCaretPositionUpdateValue,
    setPendingMorphingCaretPositionUpdateValue,
    getPreviousCaretPosition,
    getNextCaretPosition,
    getNewPositionToJump,
    activeInputValue,
    setActiveInputValue,
    pendingGroupMaxRange,
  };
};
